'use client';
import React, { useEffect, useRef } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedEls = heroRef.current?.querySelectorAll('.animate-on-enter');
    animatedEls?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-4 pt-20 pb-16 overflow-hidden"
      style={{
        backgroundImage: 'radial-gradient(at 0% 0%, #0a2a4a 0%, #050d1a 45%, #020608 100%)',
      }}
    >
      {/* Background decorative blobs */}
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00b4ff 0%, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-15 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #ff6b35 0%, transparent 70%)' }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00b4ff 0%, transparent 60%)' }}
      />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(0,180,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,180,255,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Location badge */}
        <div
          className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8"
          style={{
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            border: '1px solid rgba(255, 107, 53, 0.45)',
            transitionDelay: '0ms',
          }}
        >
          <span
            className="font-montserrat font-bold uppercase text-sm"
            style={{ color: '#ff6b35', letterSpacing: '1.4px' }}
          >
            SERIOUS SWAG &middot; PREMIUM CORPORATE MERCHANDISE
          </span>
        </div>

        {/* Large background text */}
        <div
          className="font-montserrat font-black text-white select-none pointer-events-none mb-2"
          style={{
            fontSize: 'clamp(80px, 18vw, 200px)',
            lineHeight: '0.85',
            opacity: 0.06,
            letterSpacing: '-4px',
          }}
        >
          3D
        </div>

        {/* Main headline */}
        <div
          className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 -mt-8 mb-4"
          style={{ transitionDelay: '100ms' }}
        >
          <p
            className="font-montserrat font-bold uppercase mb-3"
            style={{ color: '#ff6b35', letterSpacing: '3px', fontSize: '14px' }}
          >
            Corporate Swag. Re-Engineered.
          </p>
          <h1
            className="font-montserrat font-black text-white leading-tight"
            style={{ fontSize: 'clamp(32px, 5vw, 64px)', letterSpacing: '-1px' }}
          >
            Custom Corporate Merchandise.
            <br />
            <span style={{ color: '#ff6b35' }}>Re-Engineered</span> For Serious
            <br />
            Business Leverage.
          </h1>
        </div>

        {/* Subheadline */}
        <p
          className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 max-w-2xl mx-auto mb-10 leading-relaxed"
          style={{
            color: 'rgb(136, 147, 152)',
            fontSize: 'clamp(16px, 2vw, 20px)',
            transitionDelay: '200ms',
          }}
        >
          The complete ecosystem of ultra-premium, 3D-textured merchandise engineered for local
          service businesses, hospitality brands, and corporate teams that are{' '}
          <span className="text-white font-semibold">serious about dominating their market</span>.
        </p>

        {/* CTA Buttons */}
        <div
          className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
          style={{ transitionDelay: '300ms' }}
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300"
            style={{
              backgroundColor: '#ff6b35',
              border: '2px solid #ff6b35',
              letterSpacing: '0.4px',
              fontSize: '15px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLElement).style.color = '#ff6b35';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '#ff6b35';
              (e.currentTarget as HTMLElement).style.color = '#ffffff';
            }}
          >
            Get a Quote
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <a
            href="#product-suite"
            className="inline-flex items-center gap-2 font-montserrat font-bold uppercase text-white rounded px-8 py-4 transition-all duration-300"
            style={{
              backgroundColor: 'transparent',
              border: '2px solid rgba(255,255,255,0.4)',
              letterSpacing: '0.4px',
              fontSize: '15px',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = '#ff6b35';
              (e.currentTarget as HTMLElement).style.color = '#ff6b35';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.4)';
              (e.currentTarget as HTMLElement).style.color = '#ffffff';
            }}
          >
            Explore the Suite
          </a>
        </div>

        {/* Scroll indicator */}
        <div
          className="animate-on-enter opacity-0 translate-y-8 transition-all duration-700 flex flex-col items-center gap-2"
          style={{ transitionDelay: '400ms' }}
        >
          <span
            className="font-montserrat uppercase text-xs"
            style={{ color: 'rgb(136, 147, 152)', letterSpacing: '1.2px' }}
          >
            Scroll to explore
          </span>
          <div
            className="w-6 h-10 rounded-full border-2 flex items-start justify-center p-1"
            style={{ borderColor: 'rgb(136, 147, 152)' }}
          >
            <div
              className="w-1 h-2 rounded-full ts-scroll-dot"
              style={{ backgroundColor: '#ff6b35' }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
